import type { MachineSummary, Project, RecentChat, Workspace } from "@racler/protocol";
import type { RecentProject } from "./recent.ts";

export interface DashboardProject {
  machineId: string;
  projectId: string;
  name: string;
  workspaceName?: string;
  machineName: string;
  path?: string;
  online: boolean;
  lastActivity: number; // ms timestamp, 0 if unknown
}

export interface AssembleProjectsParams {
  machines: MachineSummary[] | null;
  machineData: Map<string, { projects: Project[]; workspaces: Workspace[] }>;
  recent: RecentProject[];
  chats: RecentChat[] | null | undefined;
}

/**
 * Assembles a deduplicated, sorted list of projects registered on machines.
 *
 * Source of truth: only projects currently registered on machines (`machineData`) are included.
 * Historical records (recent browser visits, chat history, usage) never resurrect or create projects
 * that have been removed from the registered source of truth.
 *
 * Meaningful activity is derived from:
 * 1. Project opened in this browser (`recentProject.openedAt`)
 * 2. Latest chat in that project (`chat.updatedAt`)
 * Sorted by most recent meaningful activity first.
 */
export function assembleDashboardProjects({
  machines,
  machineData,
  recent,
  chats,
}: AssembleProjectsParams): DashboardProject[] {
  const machineMap = new Map((machines ?? []).map((m) => [m.id, m]));
  const map = new Map<string, DashboardProject>();

  // 1. Projects and workspaces reported by machines (authoritative registered project source of truth)
  for (const [mId, data] of machineData.entries()) {
    const m = machineMap.get(mId);
    const wsMap = new Map(data.workspaces.map((w) => [w.id, w]));
    for (const p of data.projects) {
      const key = `${mId}:${p.id}`;
      const ws = wsMap.get(p.workspaceId);
      map.set(key, {
        machineId: mId,
        projectId: p.id,
        name: p.name,
        workspaceName: ws?.name,
        machineName: m?.name ?? "Machine",
        path: p.path,
        online: m?.online ?? true,
        lastActivity: 0,
      });
    }
  }

  // 2. Recent projects recorded in localStorage: enrich lastActivity and metadata for active projects only.
  // Never derive active Project existence purely from recent activity or stale cached records.
  for (const r of recent) {
    const key = `${r.machineId}:${r.projectId}`;
    const existing = map.get(key);
    if (existing) {
      existing.lastActivity = Math.max(existing.lastActivity, r.openedAt);
      if (!existing.workspaceName && r.workspaceName) existing.workspaceName = r.workspaceName;
      if (!existing.path && r.path) existing.path = r.path;
    }
  }

  // 3. Chat activity: update lastActivity for active projects only.
  // Historical chat records may reference an old Project ID, but must never resurrect removed projects in active UI.
  for (const c of chats ?? []) {
    const key = `${c.machineId}:${c.projectId}`;
    const ts = Date.parse(c.updatedAt);
    const validTs = Number.isNaN(ts) ? 0 : ts;
    const existing = map.get(key);
    if (existing) {
      existing.lastActivity = Math.max(existing.lastActivity, validTs);
      if (c.projectName && !existing.name) existing.name = c.projectName;
    }
  }

  // Convert to array and sort by most recent meaningful activity
  const list = Array.from(map.values());
  list.sort((a, b) => {
    if (b.lastActivity !== a.lastActivity) {
      return b.lastActivity - a.lastActivity;
    }
    return a.name.localeCompare(b.name);
  });
  return list;
}

/** Filter projects by search query across name, workspaceName, machineName, path. */
export function filterProjects(projects: DashboardProject[], query: string): DashboardProject[] {
  const q = query.trim().toLowerCase();
  if (!q) return projects;
  return projects.filter((p) => {
    return (
      p.name.toLowerCase().includes(q) ||
      (p.workspaceName ? p.workspaceName.toLowerCase().includes(q) : false) ||
      p.machineName.toLowerCase().includes(q) ||
      (p.path ? p.path.toLowerCase().includes(q) : false)
    );
  });
}

/** Filter chats by search query across chat title and project name. */
export function filterChats(
  chats: RecentChat[],
  projectNames: Map<string, string>,
  query: string,
): RecentChat[] {
  const q = query.trim().toLowerCase();
  if (!q) return chats;
  return chats.filter((c) => {
    const pName = c.projectName || projectNames.get(`${c.machineId}:${c.projectId}`) || "";
    return c.title.toLowerCase().includes(q) || pName.toLowerCase().includes(q);
  });
}

export function sortChats(chats: RecentChat[]): RecentChat[] {
  return [...chats].sort((a, b) => {
    const tb = Date.parse(b.updatedAt);
    const ta = Date.parse(a.updatedAt);
    return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
  });
}

export interface PaginationResult<T> {
  pageItems: T[];
  safePage: number;
  totalPages: number;
  start: number;
  end: number;
  total: number;
  canPrev: boolean;
  canNext: boolean;
}

export function paginate<T>(items: T[], page: number, pageSize = 8): PaginationResult<T> {
  const total = items.length;
  if (total === 0) {
    return {
      pageItems: [],
      safePage: 1,
      totalPages: 1,
      start: 0,
      end: 0,
      total: 0,
      canPrev: false,
      canNext: false,
    };
  }
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  const end = Math.min(start + pageSize, total);
  const pageItems = items.slice(start, end);

  return {
    pageItems,
    safePage,
    totalPages,
    start: start + 1,
    end,
    total,
    canPrev: safePage > 1,
    canNext: safePage < totalPages,
  };
}
